import { Alert, Box, Button, CircularProgress, FormControl, InputLabel, Stack, Typography } from "@mui/material";
import { produce } from "immer";
import React, { useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { assetService } from "../../../Core/Api/services";
import { CreateStandaloneRequest } from "../../../Core/Api/Types/create-standalone-request";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import MetadataInputIndex from "../../MetadataInput/metadata-input-index";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { CreateNewDbMetadataValue } from "../../../Core/Utils/asset-object-utils";
import { useTranslation } from "react-i18next";

export type ModalCreateContentElementProps = {
  titleId: number;
  onConfirm?: (comment?: string) => void;
};

export type ModalCreateContentProps = CommonModalProps<ModalCreateContentElementProps>;

const ModalCreateContent = React.forwardRef<any, ModalCreateContentProps>((props, ref) => {
  const { closeModal, onConfirm, ...modalProps } = props;
  const { t } = useTranslation();
  const [metas, setMetas] = useState<DbMetadataFieldInfos[]>([]);
  const [obj, setObj] = useState<DbAssetObjectInfos>();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const { titleId } = modalProps;
  const [isLoading, setIsLoading] = useState(true);

  const handleConfirmClick = () => {
    if (obj)
      assetService
        .CreateStandalone(
          new CreateStandaloneRequest({
            objs: [{ ...obj, metadata: obj.metadata?.filter((x) => x.value !== undefined) ?? null }],
          })
        )
        .then((response) => {
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.contentCreated"), "success");
            closeModal();
          } else {
            enqueueCustomSnackbar(response.message as string, "error");
          }
        });
  };

  const handleCancelClick = () => {
    closeModal();
  };

  const updateRequest = (value: any, metaId: number, valueIndex?: number) => {
    setObj(
      produce((draft) => {
        if (!draft || !draft.metadata) return;
        let match = draft.metadata.find((x) => x.id === metaId);
        if (!match) return;
        if (value) {
          if (Array.isArray(value)) {
            match = CreateNewDbMetadataValue(
              metaId,
              match?.fieldType!,
              match?.fieldName!,
              value.map((a) => a.value)
            );
          } else match = CreateNewDbMetadataValue(metaId, match?.fieldType!, match?.fieldName!, value.value);
        } else match = CreateNewDbMetadataValue(metaId, match?.fieldType!, match?.fieldName!, undefined);

        draft.metadata = [...draft.metadata.filter((x) => x.id !== metaId), match];
      })
    );
  };

  React.useEffect(() => {
    assetService.GetMetadataFieldsFlat().then((response) => {
      if (response && response.metadataFields) {
        let filteredMetas = response.metadataFields
          ?.filter((f) => f.isEditable && f.layer === "Content")
          .sort(function (a, b) {
            return a.label!.localeCompare(b.label!);
          });
        if (filteredMetas) {
          let dataPost = {
            objs: [
              {
                parentObjectId: titleId,
                layer: ObjectLayer.Content,
                type: ObjectType.GraphSkeleton,
                metadata: Array(),
              },
            ],
          };
          for (let i = 0; i < filteredMetas.length; i++) {
            dataPost.objs[0].metadata.push(
              CreateNewDbMetadataValue(filteredMetas[i].id, filteredMetas[i].baseType!, "", undefined)
            );
          }
          setObj(dataPost.objs[0] as DbAssetObjectInfos);
          setMetas(filteredMetas);
          setIsLoading(false);
        }
      }
    });
  }, []);

  const isValid = useMemo((): boolean => {
    if (!obj) return false;

    if (!obj.metadata) return false;

    let count = obj.metadata!.length;
    let missing = 0;

    for (let k = 0; k < obj.metadata!.length; ++k) {
      if (!obj.metadata![k].value) ++missing;
    }

    if (missing === count) return false;

    if (obj.metadata.find((x) => x.id === metas.find((x) => x.tagName === "contentType")!.id)!.value === undefined)
      return false;

    return true;
  }, [obj, obj?.metadata]);

  return (
    <Modal
      title={t("modal:title.createContentForTitle", { titleId })}
      reference={ref}
      closeModal={handleCancelClick}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick} disabled={!isValid}>
            {t("common:create")}
          </Button>
        </Stack>
      }
    >
      {!isLoading ? (
        <Stack spacing={1}>
          <Stack spacing={1} mb={1.5}>
            <Typography variant="h6" component="span" color="text.secondary">
              {t("modal:modalCreateContent.subTitle")}
            </Typography>
          </Stack>

          {metas.map((meta) => (
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="customer-name">
                {meta.label} {meta.tagName === "contentType" && "*"}
              </InputLabel>
              <MetadataInputIndex
                onChange={(value, fieldId, valueIndex) => updateRequest(value, fieldId, valueIndex)}
                data={meta}
                value={obj!.metadata?.find((a) => a.id === meta.id)}
              />
            </FormControl>
          ))}

          <Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              {t("alert:warning.afterCreationAction")}
            </Alert>
          </Box>
        </Stack>
      ) : (
        <CircularProgress />
      )}
    </Modal>
  );
});

export default ModalCreateContent;
